import * as Sentry from "@sentry/react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import AdminRoute from "@/components/admin-route";
import { OrganizationProvider, OrganizationGuard } from "@/contexts/OrganizationContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import PhoneEntry from "@/pages/phone-entry";
import ResetPin from "@/pages/reset-pin";
import VerifyEmail from "@/pages/verify-email";
import ClientPortal from "@/pages/client-portal";
import CustomerEntry from "@/pages/customer-entry";
import SellerDashboard from "@/pages/seller-dashboard";
import AdminDashboard from "@/pages/admin-dashboard";
import AccountManagement from "@/pages/account-management";
import AccountSummary from "@/pages/account-summary";
import Products from "@/pages/products";
import Settings from "@/pages/settings";
import OversightConsole from "@/pages/oversight-console";
import History from "@/pages/history";
import PrivacyPolicy from "@/pages/privacy-policy";
import TermsOfService from "@/pages/terms-of-service";
import OnboardingWizard from "@/pages/onboarding-wizard";
import SuperAdminOnboarding from "@/pages/super-admin-onboarding";
import AccountDeletion from "@/pages/account-deletion";
import NotFound from "@/pages/not-found";
import { DesignDemo } from "@/pages/design-demo";
import SuperAdmin from "@/pages/super-admin";
import AppInfo from "@/pages/app-info";
import OrderSummary from "@/pages/order-summary";
import KioskEntry from "@/pages/kiosk-entry";
import DemoPortal from "@/pages/demo-portal";
import DemoAdminView from "@/pages/demo-admin-view";
import KioskHelp from "@/pages/kiosk-help";
import SetPassword from "@/pages/set-password";
import { Redirect } from "wouter";
import AppLaunch from "@/pages/app-launch";
import { isNativeApp } from "@/lib/native/platform";

function AppRoutes() {
  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />
      <Switch>
        {/* Global routes (no org context needed) */}
        {/* Native launch screen (Capacitor shell entry; also renders on web for
            dev/screenshots). Must precede the /:customUrl catch-all. */}
        <Route path="/app" component={AppLaunch} />
        <Route path="/super-admin" component={SuperAdmin} />
        {/* Pre-tenant intake review. Separate page rather than a super-admin tab:
            everything in super-admin.tsx is scoped to an existing organization and
            these submissions predate one. */}
        <Route path="/super-admin/onboarding" component={SuperAdminOnboarding} />
        <Route path="/set-password" component={SetPassword} />
        <Route path="/info" component={AppInfo} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/account-deletion" component={AccountDeletion} />
        {/* Pre-tenant onboarding intake. Public and token-linked — the prospect has
            no account and no org yet, so this must sit above the /:customUrl catch-all. */}
        <Route path="/onboard/:token" component={OnboardingWizard} />
        <Route path="/demo/design-preview" component={DesignDemo} />
        
        {/* Legacy admin routes (default org) */}
        <Route path="/admin/login">{() => <Redirect to="/" />}</Route>
        <Route path="/admin" component={() => <AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/accounts" component={() => <AdminRoute><AccountManagement /></AdminRoute>} />
        <Route path="/admin/accounts/:id" component={() => <AdminRoute><AccountSummary /></AdminRoute>} />
        <Route path="/admin/products" component={() => <AdminRoute><Products /></AdminRoute>} />
        <Route path="/admin/orders" component={() => <AdminRoute><SellerDashboard /></AdminRoute>} />
        <Route path="/admin/agent" component={() => <AdminRoute><OversightConsole /></AdminRoute>} />
        <Route path="/admin/settings" component={() => <AdminRoute><Settings /></AdminRoute>} />
        <Route path="/admin/history" component={() => <AdminRoute><History /></AdminRoute>} />
        <Route path="/admin/kiosk-help" component={() => <AdminRoute><KioskHelp /></AdminRoute>} />
        
        {/* Homepage - Client Portal (web). The Capacitor shell instead opens on
            the branded multi-vendor launch screen — its 4.3 store defense. */}
        <Route path="/">{() => (isNativeApp() ? <Redirect to="/app" /> : <ClientPortal />)}</Route>
        <Route path="/dashboard" component={SellerDashboard} />
        <Route path="/accounts" component={AccountManagement} />
        <Route path="/accounts/:id" component={AccountSummary} />
        <Route path="/products" component={Products} />
        <Route path="/orders" component={SellerDashboard} />
        
        {/* Org-scoped routes use /org/:orgSlug prefix for clear separation */}
        {/* All org-scoped routes are wrapped with OrganizationGuard for error handling */}
        {/* Customer-facing pages */}
        <Route path="/org/:orgSlug" component={() => <OrganizationGuard><PhoneEntry /></OrganizationGuard>} />
        <Route path="/org/:orgSlug/reset-pin" component={() => <OrganizationGuard><ResetPin /></OrganizationGuard>} />
        <Route path="/org/:orgSlug/verify-email" component={() => <OrganizationGuard><VerifyEmail /></OrganizationGuard>} />
        <Route path="/org/:orgSlug/order/:customUrl" component={() => <OrganizationGuard><CustomerEntry /></OrganizationGuard>} />
        <Route path="/org/:orgSlug/order-summary/:accountId/:orderId" component={() => <OrganizationGuard><OrderSummary /></OrganizationGuard>} />
        <Route path="/org/:orgSlug/kiosk" component={() => <OrganizationGuard><KioskEntry /></OrganizationGuard>} />
        
        {/* Org-scoped admin routes: /org/:orgSlug/admin/... */}
        <Route path="/org/:orgSlug/admin/login">{() => <Redirect to="/" />}</Route>
        <Route path="/org/:orgSlug/admin" component={() => <OrganizationGuard><AdminRoute><AdminDashboard /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/accounts" component={() => <OrganizationGuard><AdminRoute><AccountManagement /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/accounts/:id" component={() => <OrganizationGuard><AdminRoute><AccountSummary /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/products" component={() => <OrganizationGuard><AdminRoute><Products /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/orders" component={() => <OrganizationGuard><AdminRoute><SellerDashboard /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/agent" component={() => <OrganizationGuard><AdminRoute><OversightConsole /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/settings" component={() => <OrganizationGuard><AdminRoute><Settings /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/history" component={() => <OrganizationGuard><AdminRoute><History /></AdminRoute></OrganizationGuard>} />
        <Route path="/org/:orgSlug/admin/kiosk-help" component={() => <OrganizationGuard><AdminRoute><KioskHelp /></AdminRoute></OrganizationGuard>} />
        
        {/* Demo portal — must be before the /:customUrl catch-all */}
        <Route path="/demo" component={DemoPortal} />
        <Route path="/demo/admin/order/:orderId" component={DemoAdminView} />

        {/* Legacy customer URL (backward compatibility - catches /jon-lucas style URLs) */}
        <Route path="/:customUrl" component={CustomerEntry} />
        
        <Route component={NotFound} />
      </Switch>
    </div>
  );
}

// Branded fallback shown when an unhandled render error escapes a route.
// Sentry.ErrorBoundary auto-captures the underlying exception — no manual
// captureException needed. The error/stack are intentionally never displayed
// (would leak server paths or internals); the prop is wired for programmatic
// use only.
function ErrorFallback({ resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="pt-6">
          <div className="flex mb-4 gap-2 items-center">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            FreshOrder hit an unexpected error. Please refresh the page to continue.
            If this keeps happening, contact support.
          </p>
          <button
            onClick={() => {
              resetError();
              window.location.reload();
            }}
            className="mt-6 px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors w-full"
            data-testid="error-boundary-refresh"
          >
            Refresh Page
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <OrganizationProvider>
            <AdminAuthProvider>
              <Toaster />
              <AppRoutes />
            </AdminAuthProvider>
          </OrganizationProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
